/**
 * DesmosErrorBoundary - Error boundary for Desmos calculator components
 *
 * Catches JavaScript errors in Desmos components and displays a fallback UI
 * instead of crashing the entire page.
 */

import { Component } from 'react'
import PropTypes from 'prop-types'
import { createLogger } from '../utils/logger'

const logger = createLogger('DesmosErrorBoundary')

class DesmosErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    logger.error('[Desmos] Error boundary caught:', error, errorInfo)

    // Could send to error tracking service here
    if (this.props.onError) {
      this.props.onError(error, errorInfo)
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default error UI
      return (
        <div
          className="flex flex-col items-center justify-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800"
          style={{ minHeight: this.props.minHeight || 200 }}
          role="alert"
          aria-live="assertive"
        >
          <svg
            className="w-8 h-8 text-red-500 dark:text-red-400 mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <p className="text-red-600 dark:text-red-400 text-sm font-medium mb-2">
            Calculator failed to load
          </p>
          <p className="text-red-500 dark:text-red-500 text-xs mb-3">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <button
            onClick={this.handleRetry}
            className="px-3 py-1.5 text-sm font-medium rounded-lg bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors"
          >
            Try Again
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

DesmosErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  minHeight: PropTypes.number,
  onError: PropTypes.func
}

export default DesmosErrorBoundary
